import { useEffect, useLayoutEffect, useState, type ReactElement } from 'react'
import { createPortal } from 'react-dom'

import { cn } from 'src/shared/components/ui/utils'

import {
  ONBOARDING_ENABLED,
  ONBOARDING_LAYER_Z_INDEX,
  ONBOARDING_SHOW_DELAY_MS,
  ONBOARDING_TARGET_POLL_INTERVAL_MS,
} from '../consts'
import type { OnboardingAnchorProps } from '../types'
import {
  computeOnboardingCardLayout,
  getOnboardingRingBox,
  getScrollableAncestors,
} from '../utils'
import { DefaultOnboardingTooltip } from './DefaultOnboardingTooltip'
import { OnboardingConnector } from './OnboardingConnector'
import { SpotlightOverlay } from './SpotlightOverlay'

/**
 * Renders the spotlight + tooltip in a portal. Keeps layout in sync via
 * `ResizeObserver` on the anchor, its ancestor chain (split panes, flex
 * columns), and document root; scroll listeners on
 * window and scrollable ancestors (e.g. Radix viewports), and rAF-batched
 * remeasures. Pass `measureKey` when React updates without touching measured
 * geometry.
 */
export const OnboardingAnchor = ({
  activeStep,
  onAcknowledge,
  measureKey,
}: OnboardingAnchorProps): ReactElement | null => {
  const [rect, setRect] = useState<DOMRect | null>(null)
  const [readyToShow, setReadyToShow] = useState(false)
  /** Bumps when the anchor node appears after initial mount so listeners attach */
  const [anchorEpoch, setAnchorEpoch] = useState(0)
  const targetId = activeStep?.elementId

  useEffect(() => {
    if (!targetId || !activeStep) {
      setReadyToShow(false)
      return
    }
    setReadyToShow(false)
    const t = window.setTimeout(
      () => setReadyToShow(true),
      ONBOARDING_SHOW_DELAY_MS
    )
    return () => window.clearTimeout(t)
  }, [targetId, measureKey])

  useEffect(() => {
    setAnchorEpoch(0)
  }, [targetId, measureKey])

  useLayoutEffect(() => {
    if (!targetId || !readyToShow) {
      if (!targetId) setRect(null)
      return
    }

    let raf = 0
    const scheduleUpdate = () => {
      if (raf) cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        raf = 0
        const el = document.getElementById(targetId)
        setRect(el ? el.getBoundingClientRect() : null)
      })
    }

    const update = () => {
      const el = document.getElementById(targetId)
      setRect(el ? el.getBoundingClientRect() : null)
    }

    update()

    let interval: ReturnType<typeof setInterval> | undefined
    if (!document.getElementById(targetId)) {
      interval = setInterval(() => {
        if (document.getElementById(targetId)) {
          update()
          setAnchorEpoch((e) => e + 1)
          if (interval) clearInterval(interval)
        }
      }, ONBOARDING_TARGET_POLL_INTERVAL_MS)
    } else {
      document.getElementById(targetId)?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      })
    }

    const observed = document.getElementById(targetId)
    const targetRo =
      observed &&
      new ResizeObserver(() => {
        scheduleUpdate()
      })
    if (observed && targetRo) targetRo.observe(observed)

    const rootRo = new ResizeObserver(() => {
      scheduleUpdate()
    })
    rootRo.observe(document.documentElement)

    /** Split-pane / flex reflow changes column width without resizing the viewport or the chip. */
    const layoutChainRo = new ResizeObserver(() => {
      scheduleUpdate()
    })
    let ancestor: HTMLElement | null = observed?.parentElement ?? null
    while (ancestor) {
      layoutChainRo.observe(ancestor)
      ancestor = ancestor.parentElement
    }

    const scrollParents = observed ? getScrollableAncestors(observed) : []
    const scrollParentHandlers = scrollParents.map((node) => {
      const fn = () => scheduleUpdate()
      node.addEventListener('scroll', fn, true)
      return { node, fn }
    })

    const onWindowScroll = () => scheduleUpdate()
    const onWindowResize = () => scheduleUpdate()
    const onVvResize = () => scheduleUpdate()
    const onVvScroll = () => scheduleUpdate()

    window.addEventListener('scroll', onWindowScroll, true)
    window.addEventListener('resize', onWindowResize)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onVvResize)
      window.visualViewport.addEventListener('scroll', onVvScroll)
    }

    return () => {
      if (interval) clearInterval(interval)
      if (raf) cancelAnimationFrame(raf)
      targetRo?.disconnect()
      rootRo.disconnect()
      layoutChainRo.disconnect()
      for (const { node, fn } of scrollParentHandlers) {
        node.removeEventListener('scroll', fn, true)
      }
      window.removeEventListener('scroll', onWindowScroll, true)
      window.removeEventListener('resize', onWindowResize)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onVvResize)
        window.visualViewport.removeEventListener('scroll', onVvScroll)
      }
    }
  }, [targetId, measureKey, readyToShow, anchorEpoch])

  if (
    !ONBOARDING_ENABLED ||
    typeof document === 'undefined' ||
    !readyToShow ||
    !activeStep ||
    !rect
  ) {
    return null
  }

  if (!activeStep.renderTooltip && activeStep.content == null) {
    return null
  }

  const titleId = `onboarding-tooltip-${activeStep.elementId}`
  const tooltipProps = {
    onAcknowledge,
    titleId,
    activeStep,
  }

  const panel = activeStep.renderTooltip ? (
    activeStep.renderTooltip(tooltipProps)
  ) : (
    <DefaultOnboardingTooltip {...tooltipProps} content={activeStep.content!} />
  )

  const ring = getOnboardingRingBox(rect)
  const layout = computeOnboardingCardLayout(rect, window)

  return createPortal(
    <div
      className="pointer-events-none fixed inset-0"
      style={{ zIndex: ONBOARDING_LAYER_Z_INDEX }}
    >
      <SpotlightOverlay ring={ring} />
      <div
        className={cn(
          'pointer-events-none absolute border-2 border-violet-500 shadow-[0_0_0_3px_rgba(255,255,255,0.85)] onboarding-ring-pulse',
          activeStep.ringClassName ?? 'rounded-2xl'
        )}
        style={{
          left: ring.left,
          top: ring.top,
          width: ring.width,
          height: ring.height,
        }}
        aria-hidden
      />
      <OnboardingConnector
        key={activeStep.elementId}
        originX={layout.originX}
        originY={layout.originY}
        tipX={layout.tipX}
        tipY={layout.tipY}
      />
      <div
        className="pointer-events-auto absolute w-[min(280px,calc(100vw-32px))]"
        style={{
          left: layout.cardLeft,
          top: layout.cardTop,
        }}
      >
        {panel}
      </div>
    </div>,
    document.body
  ) as ReactElement
}
